import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import type { Store as StoreService } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import { saveRecord } from 'tio-data/requests/save-record';
import type SessionContextService from 'tio-employee/services/session-context';
import type { AuthenticatedSyfRouteModel } from 'tio-employee/routes/authenticated/syf';
import { getTranslationKeyForReportedSalaryForMatchPlan } from 'tio-common/utils/syf';
import PostEnrollment from 'tio-employee/components/syf/post-enrollment';
import PageHeader from 'tio-ui/components/page-header';
import type { EmailAddressExtension as EmailAddressModel } from 'tio-data/data/resources/email-address.ext';
import type Owner from '@ember/owner';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { VStack } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import { currencyStringToNumber } from 'tio-common/utils/format';

interface SyfPostEnrollmentRouteComponentSignature {
  Blocks: {
    default: [];
  };
  Args: {
    model: AuthenticatedSyfRouteModel;
  };
}

export default class SyfPostEnrollmentRouteComponent extends Component<SyfPostEnrollmentRouteComponentSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked emailAddress?: EmailAddressModel;

  constructor(owner: Owner, args: SyfPostEnrollmentRouteComponentSignature['Args']) {
    super(owner, args);

    if (this.args.model.matchParticipant.emailAddress?.isPersonal) {
      // if found, register existing personal email address as tracked var
      this.emailAddress = this.args.model.matchParticipant.emailAddress;
    } else if (this.sessionContext.user?.personalEmail) {
      this.emailAddress = this.sessionContext.user?.personalEmail;
    }
  }

  submit = dropTask(async (email: string, reportedSalary: string) => {
    const participant = this.args.model.matchParticipant;
    const savedEmail = await this.#savePersonalEmail(email.trim());

    const linked = participant.emailAddress;
    if (savedEmail) participant.emailAddress = savedEmail;
    participant.reportedSalary = currencyStringToNumber(reportedSalary);

    try {
      await saveRecord(this.store, participant);
    } catch (error) {
      participant.rollbackAttributes();
      participant.emailAddress = linked;
      console.error(error);
      this.notifications.add(this.intl.t('syf.post_enrollment.save_error'), {
        appearance: 'error',
      });
      return;
    }

    if (!savedEmail) {
      this.notifications.add(this.intl.t('syf.post_enrollment.email_save_error'), {
        appearance: 'warning',
      });
    }
    this.router.transitionTo('authenticated.syf.dashboard');
  });

  async #savePersonalEmail(email: string) {
    const existing = this.emailAddress;
    if (existing && !existing.isNew && existing.email === email) return existing;

    const emailAddress =
      existing ??
      this.store.createRecord<EmailAddressModel>('email-address', {
        contactType: 'PERSONAL',
        user: this.sessionContext.user,
        email,
        isActive: true,
      });
    emailAddress.email = email;

    try {
      await saveRecord(this.store, emailAddress);
    } catch (error) {
      console.error(error);
      if (emailAddress.isNew) {
        emailAddress.unloadRecord();
        this.emailAddress = undefined;
      } else {
        emailAddress.rollbackAttributes();
      }
      return undefined;
    }

    this.emailAddress = emailAddress;
    return emailAddress;
  }

  <template>
    <VStack>
      <PageHeader @a11yTitle={{t "syf.post_enrollment.header"}} @sticky={{false}} />
      <PostEnrollment
        @reportedSalaryFieldLabel={{t
          (getTranslationKeyForReportedSalaryForMatchPlan @model.matchParticipant.matchPlan)
        }}
        @reportedSalary={{@model.matchParticipant.reportedSalary}}
        @emailAddress={{if this.emailAddress this.emailAddress.email ""}}
        @loading={{this.submit.isRunning}}
        @onSubmit={{this.submit.perform}}
      />
    </VStack>
  </template>
}
